import { get, orderBy } from 'lodash';

import { makeLabel } from '../MappedLabel/MappedLabel';
import { getFirmwareUpdateFlag, getShowConfigUpdate, getShowTechnicalInfoFieldsFlag } from '../../services/AuthenticationRules';
import hasAccess, {
  accountsAccessId,
  chademoAccessId,
  countriesAccessId,
  customerAdminToolsMenuAccessId,
  enclosuresAccessId,
  failuresMenuAccessId,
  fieldMappingMenuAccessId,
  firmwareVersionsMenuAccessId,
  isDirectlySupervisor,
  maintainersAccessId,
  ownersAccessId,
  parametersSchemaMenuAccessId,
  productLevelAccessId,
  productTypesAccessId,
  productVersionsAccessId,
  rolesAccessId,
  ssoAccountAdminToolsMenuAccessId,
  supervisorsAccessId,
  telemetryMetricsMenuAccessId,
  viewChargerConfigsId,
  viewFirmwareUpdatePageAccessId,
} from '../../services/HasAccess';
import {
  isChargingSessionReportAvailable,
  isDeliveredEnergyReportAvailable,
  isEnergyMixReportAvailable,
  isFaultsReportAvailable,
  isFWVersionsReportAvailable,
  isPlazaReportAvailable,
  isPowerMonitoringReportAvailable,
} from '../appConfig/selector';
import { EDGE_DEMO_URL_FLAG } from '../../constants/commonConstants';
import { isEdgeDemo } from '../../resources/helper';

export const getChargersMenuItems = (config, user) => {
  const labels = get(config, 'ui_labels');

  const items = [
    {
      label: makeLabel(labels, 'mainMenu/chargers/chargerInfo', 'Charger Info'),
      path: '/chargers/chargers_list',
    },
  ];

  if (getFirmwareUpdateFlag(config)) {
    items.push({
      label: makeLabel(labels, 'mainMenu/chargers/fwUpdate', 'FW Update'),
      path: '/chargers/multiple_fw_update',
      disabled: !hasAccess(viewFirmwareUpdatePageAccessId, get(user, 'currentPermissions'), config),
    });
  }

  if (getShowConfigUpdate(config)) {
    items.push({
      label: makeLabel(labels, 'mainMenu/chargers/configUpdate', 'Configuration Update'),
      path: '/chargers/configuration_update',
      disabled: !hasAccess(viewChargerConfigsId, get(user, 'currentPermissions'), config),
    });
  }

  return orderBy(items, [(item) => item.label.toLowerCase()]);
};

export const getDemoEdgeMenuItems = (config, user) => {
  const hideItems = !isDirectlySupervisor(user) && !isEdgeDemo();

  if (hideItems) return [];

  return [
    {
      label: 'Edge Controllers',
      path: `/demoEdge/controllers?${EDGE_DEMO_URL_FLAG}`,
      levelPath: 'demoEdge',
    },
    {
      label: 'FW Updates',
      path: `/demoEdge/fw-updates?${EDGE_DEMO_URL_FLAG}`,
      levelPath: 'demoEdge',
    },
    {
      label: 'Tasks',
      path: `/demoEdge/tasks?${EDGE_DEMO_URL_FLAG}`,
      levelPath: 'demoEdge',
    },
  ];
};

export const getStandardReportsMenuItems = (config) => {
  const labels = get(config, 'ui_labels');

  const items = [];

  if (isChargingSessionReportAvailable(config, '')) {
    items.push({
      path: '/reports/sessions',
      label: makeLabel(labels, 'mainMenu/reports/chargingSessions', 'Charging Sessions'),
    });
  }

  if (isFaultsReportAvailable(config, '')) {
    items.push({
      path: '/reports/faults',
      label: makeLabel(labels, 'mainMenu/reports/faults', 'Faults'),
    });
  }

  if (isFWVersionsReportAvailable(config, '')) {
    items.push({
      path: '/reports/firmware_versions',
      label: makeLabel(labels, 'mainMenu/reports/fwVersions', 'FW Versions'),
    });
  }

  if (isPowerMonitoringReportAvailable(config, '')) {
    items.push({
      path: '/reports/power-monitoring',
      label: makeLabel(labels, 'mainMenu/reports/powerMonitoring', 'Power Monitoring'),
    });
  }

  return orderBy(items, [(item) => item.label.toLowerCase()]);
};

export const getExtendedReportsMenuItems = (config) => {
  const labels = get(config, 'ui_labels');

  const items = [];

  if (
    isDeliveredEnergyReportAvailable(config, '') &&
    (config.plazaAvailabilityMode === 'full' || config.plazaAvailabilityMode === 'partially')
  ) {
    items.push({
      path: '/reports/delivered_energy',
      label: makeLabel(labels, 'mainMenu/reports/deliveredEnergy', 'Delivered Energy'),
    });
  }

  if (isEnergyMixReportAvailable(config, '') && config.plazaAvailabilityMode === 'full') {
    items.push({
      path: '/reports/energy_mix',
      label: makeLabel(labels, 'mainMenu/reports/energyMix', 'Energy Mix'),
    });
  }

  if (isPlazaReportAvailable(config, '') && (config.plazaAvailabilityMode === 'full' || config.plazaAvailabilityMode === 'partially')) {
    items.push({
      path: '/reports/plaza_report',
      label: makeLabel(labels, 'mainMenu/reports/plazaReport', 'Power/Load'),
    });
  }

  return orderBy(items, [(item) => item.label.toLowerCase()]);
};

export const getReportsMenuItems = (config) => {
  const items = [].concat(getStandardReportsMenuItems(config)).concat(getExtendedReportsMenuItems(config));

  return orderBy(items, [(item) => item.label.toLowerCase()]);
};

export const getAdminMenuItems = (config, user) => {
  const labels = get(config, 'ui_labels');
  const items = [
    {
      id: accountsAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/accounts', 'Accounts'),
      path: '/admin/accounts',
    },
    {
      id: countriesAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/countries', 'Countries'),
      path: '/admin/countries',
    },
    {
      id: customerAdminToolsMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/customers', 'Customers'),
      path: '/admin/customers',
    },
    {
      id: ssoAccountAdminToolsMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/ssoAccounts', 'SSO Accounts'),
      path: '/admin/sso-accounts',
    },
    {
      id: failuresMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/faults', 'Faults'),
      path: '/admin/faults',
    },
    {
      id: fieldMappingMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/fieldMapping', 'Field Mapping'),
      path: '/admin/field-mapping',
    },
    {
      id: firmwareVersionsMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/fwUpdates', 'FW Updates'),
      path: '/admin/firmware-versions',
    },
    {
      id: maintainersAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/maintainers', 'Maintainers'),
      path: '/admin/maintainers',
    },
    {
      id: ownersAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/owners', 'Owners'),
      path: '/admin/owners',
    },
    {
      id: productTypesAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/productTypes', 'Product Types'),
      path: '/admin/product-types',
    },
    {
      id: supervisorsAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/supervisors', 'Supervisors'),
      path: '/admin/supervisors',
    },
    {
      id: rolesAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/accessLevels', 'Access Levels'),
      path: '/admin/access-levels',
    },
    {
      id: parametersSchemaMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/configurationParameters', 'Configuration Parameters'),
      path: '/admin/parameters-schema',
    },
    {
      id: telemetryMetricsMenuAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/telemetryMetrics', 'Telemetry Metrics'),
      path: '/admin/telemetry-metrics',
    },
  ];

  if (getShowTechnicalInfoFieldsFlag(config)) {
    items.push(
      {
        id: enclosuresAccessId,
        label: makeLabel(labels, 'mainMenu/adminTools/enclosures', 'Enclosures'),
        path: '/admin/enclosure',
      },
      {
        id: productLevelAccessId,
        label: makeLabel(labels, 'admin_tools/c_product_levels', 'Product Levels'),
        path: '/admin/product-level',
      },
      {
        id: productVersionsAccessId,
        label: makeLabel(labels, 'mainMenu/adminTools/productVersions', 'Product Versions'),
        path: '/admin/product-versions',
      },
    );
  }

  const chaDemoShow = get(config, 'admin.tabs.chademo.show', false);

  if (chaDemoShow) {
    items.push({
      id: chademoAccessId,
      label: makeLabel(labels, 'mainMenu/adminTools/chademoCables', 'CHAdeMO Cables'),
      path: '/admin/chademo-cable',
    });
  }

  const itemsWithUserAccess = items.filter((item) => hasAccess(item.id, get(user, 'currentPermissions'), config));

  return orderBy(itemsWithUserAccess, [(item) => item.label.toLowerCase()]);
};
